/**
 * useColumnPreferences Hook
 *
 * Manages column visibility and order preferences in localStorage.
 * Handles preset views (Essential, Standard, Complete) and custom arrangements.
 */

import { useState, useCallback, useEffect } from 'react'
import type { PresetView, ColumnPreferences, UseColumnPreferencesResult } from '../types'
import { applyPresetView, mergeColumnPreferences } from '../utils/columnSorting'

/**
 * localStorage key pattern for column preferences
 */
const STORAGE_KEY_PREFIX = 'metadataTable_'

/**
 * Get storage key for a model
 */
function getStorageKey(model: string): string {
  return `${STORAGE_KEY_PREFIX}${model}_columns`
}

/**
 * Load preferences from localStorage
 */
function loadPreferences(model: string): ColumnPreferences | null {
  try {
    const key = getStorageKey(model)
    const stored = localStorage.getItem(key)
    if (stored) {
      return JSON.parse(stored) as ColumnPreferences
    }
  } catch {
    // localStorage unavailable or corrupted - silently fall back to defaults
  }
  return null
}

/**
 * Save preferences to localStorage
 */
function savePreferences(model: string, prefs: ColumnPreferences): void {
  try {
    const key = getStorageKey(model)
    localStorage.setItem(key, JSON.stringify(prefs))
  } catch {
    // localStorage unavailable - preferences won't persist but app continues
  }
}

/**
 * Hook to manage column visibility preferences.
 *
 * @param model - Entity model name (singular)
 * @param defaultColumns - Default columns from metadata (table_default=true) - used for Essential preset
 * @param allColumns - All available columns from metadata - used for Complete preset
 * @param recommendedColumns - Recommended columns from metadata (recommended=true) - used for Standard preset
 * @returns Column preferences state and actions
 */
export function useColumnPreferences(
  model: string,
  defaultColumns: string[],
  allColumns: string[],
  recommendedColumns: string[] = []
): UseColumnPreferencesResult {
  // Initialize state from localStorage or defaults
  // NOTE: allColumns may be empty during initial render (before metadata loads).
  // We handle this by using saved columns as-is when allColumns is empty,
  // then merging properly once metadata arrives via the useEffect below.
  const [visibleColumns, setVisibleColumnsState] = useState<string[]>(() => {
    const saved = loadPreferences(model)
    if (saved) {
      // If allColumns not yet loaded, use saved columns as-is
      // (merge will happen in useEffect when metadata arrives)
      if (allColumns.length === 0) {
        return saved.visibleColumns
      }
      // Merge saved with available (handles removed columns)
      return mergeColumnPreferences(saved.visibleColumns, allColumns)
    }
    // Default to Essential preset (may be empty if metadata not loaded)
    return applyPresetView(defaultColumns, 'essential')
  })

  const [preset, setPreset] = useState<PresetView>(() => {
    const saved = loadPreferences(model)
    return saved?.preset ?? 'essential'
  })

  // Update when model changes or metadata loads
  // CRITICAL: Skip if allColumns is empty (metadata still loading).
  // This prevents a race condition where empty allColumns causes
  // mergeColumnPreferences to return [], wiping out saved preferences.
  useEffect(() => {
    // Guard: Don't run until metadata has loaded
    if (allColumns.length === 0) {
      return
    }

    const saved = loadPreferences(model)
    if (saved) {
      const mergedColumns = mergeColumnPreferences(saved.visibleColumns, allColumns)
      setVisibleColumnsState(mergedColumns)
      setPreset(saved.preset)
    } else {
      setVisibleColumnsState(applyPresetView(defaultColumns, 'essential'))
      setPreset('essential')
    }
  }, [model, defaultColumns, allColumns])

  // Update when columns change (handles initial load)
  // This effect catches the case where component mounted with empty allColumns
  // and visibleColumns was set to [] from defaults
  useEffect(() => {
    if (allColumns.length > 0 && visibleColumns.length === 0) {
      const saved = loadPreferences(model)
      if (saved) {
        const mergedColumns = mergeColumnPreferences(saved.visibleColumns, allColumns)
        if (mergedColumns.length > 0) {
          setVisibleColumnsState(mergedColumns)
          return
        }
      }
      // Fallback to essential preset
      setVisibleColumnsState(applyPresetView(defaultColumns, 'essential'))
    }
  }, [allColumns, visibleColumns.length, model, defaultColumns])

  // Set visible columns (custom arrangement)
  const setVisibleColumns = useCallback(
    (columns: string[]) => {
      setVisibleColumnsState(columns)
      setPreset('custom')
      savePreferences(model, {
        visibleColumns: columns,
        preset: 'custom',
      })
    },
    [model]
  )

  // Apply a preset view
  const applyPreset = useCallback(
    (newPreset: PresetView, columnSource: string[]) => {
      let columns: string[]

      switch (newPreset) {
        case 'essential':
          // Essential = table_default fields
          columns = defaultColumns
          break
        case 'standard':
          // Standard = recommended fields
          columns = recommendedColumns.length > 0 ? recommendedColumns : defaultColumns
          break
        case 'complete':
          // Complete = all visible fields
          columns = allColumns
          break
        case 'custom':
          // Custom = user-provided columns
          columns = columnSource
          break
        default:
          columns = columnSource
      }

      setVisibleColumnsState(columns)
      setPreset(newPreset)
      savePreferences(model, {
        visibleColumns: columns,
        preset: newPreset,
      })
    },
    [model, defaultColumns, allColumns, recommendedColumns]
  )

  // Reset to defaults (Essential preset)
  const resetToDefaults = useCallback(
    (columnSource: string[]) => {
      const columns = applyPresetView(columnSource, 'essential')
      setVisibleColumnsState(columns)
      setPreset('essential')
      savePreferences(model, {
        visibleColumns: columns,
        preset: 'essential',
      })
    },
    [model]
  )

  return {
    visibleColumns,
    preset,
    setVisibleColumns,
    applyPreset,
    resetToDefaults,
  }
}

/**
 * Clear all column preferences for a model.
 * Useful for testing or resetting user data.
 */
export function clearColumnPreferences(model: string): void {
  try {
    const key = getStorageKey(model)
    localStorage.removeItem(key)
  } catch {
    // localStorage unavailable - nothing to clear
  }
}

/**
 * Clear all MetadataTable preferences.
 */
export function clearAllColumnPreferences(): void {
  try {
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith(STORAGE_KEY_PREFIX)
    )
    keys.forEach((key) => localStorage.removeItem(key))
  } catch {
    // localStorage unavailable - nothing to clear
  }
}
